/**
 * The customer words for a capture angle's state and for what to do next.
 *
 * Wording only. Every decision behind these strings was made on the server
 * (`domain/guided_coverage_status.py`) and arrives as a code, for the same
 * reason live capture guidance does: two places deciding is two places to
 * disagree, and the one that disagrees on screen is the one the user believes.
 *
 * Two rules the copy has to keep:
 *
 * **`sufficient` is not "finished".** The threshold behind it is the
 * reconstruction engine's geometric floor - three cameras, the minimum that
 * constrains a scene - and the profile that defines it says it is not a
 * product quality threshold. So the word is "yeterli görüntü var", never "bu
 * bölge tamam".
 *
 * **Nothing is carried by colour alone.** Each state has a tone *and* a glyph
 * *and* a sentence, because this is the screen somebody uses outdoors on a
 * phone to decide whether to keep shooting.
 */

import type {
  GuidedNextAction,
  GuidedRegionStatus,
  SubjectType,
} from "@/api/types";
import { guidedRegionLabel } from "./guidedCoverage";

export type StatusTone = "neutral" | "success" | "caution" | "danger";

export interface StatusTerm {
  label: string;
  tone: StatusTone;
  /** Redundant with the tone on purpose - colour is not the only channel. */
  glyph: string;
  /** One sentence saying what the state means, for the detail panel. */
  meaning: string;
}

export const REGION_STATUS_TERMS: Record<GuidedRegionStatus, StatusTerm> = {
  missing: {
    label: "Eksik",
    tone: "danger",
    glyph: "○",
    meaning: "Bu açı için işaretlenmiş dosya yok.",
  },
  problematic: {
    label: "Eksik · Kalite sorunlu",
    tone: "danger",
    glyph: "!",
    meaning:
      "Ölçülen dosyaların çoğu kalite değerlendirmesinden geçemedi. Sayı değil, kullanılabilirlik eksik.",
  },
  weak: {
    label: "Kısmi",
    tone: "caution",
    glyph: "◐",
    meaning:
      "Kullanılabilir dosya var ama rekonstrüksiyonun geometrik alt sınırının altında.",
  },
  unverified: {
    label: "Ölçülmedi",
    tone: "neutral",
    glyph: "?",
    meaning:
      "Dosyalar var, kaliteleri henüz ölçülmedi. Bu iyi ya da kötü demek değildir.",
  },
  sufficient: {
    label: "İyi",
    tone: "success",
    glyph: "✓",
    meaning:
      "Bu açıda çalışmaya yetecek kadar kullanılabilir görüntü var. Bu, bölgenin bittiği ya da sonucun iyi olacağı anlamına gelmez.",
  },
};

/**
 * The one thing to do now, as a sentence.
 *
 * Every number in these strings comes from the server's own counts, so the
 * banner cannot say "4 more" while the region card says something else.
 */
export function nextActionText(
  action: GuidedNextAction,
  subjectType: SubjectType,
): { title: string; detail?: string } {
  const name = guidedRegionLabel(subjectType, action.region);
  switch (action.kind) {
    case "start_capturing":
      return {
        title: "Henüz fotoğraf yok.",
        detail:
          "Bir açı seçip çekmeye başlayın. Telefonla çekerken seçtiğiniz açı fotoğrafla birlikte kaydedilir.",
      };
    case "capture_region":
      return {
        title: `${name} bölgesi için fotoğraf çekin.`,
        detail: "Bu açıya işaretlenmiş hiç dosya yok.",
      };
    case "recapture_region":
      return {
        title: `${name} bölgesini yeniden çekin.`,
        detail:
          `Bu açıdaki ${action.media_count} dosya kalite değerlendirmesinden geçemedi.` +
          (action.shortfall > 0
            ? ` En az ${action.shortfall} kullanılabilir fotoğraf daha gerekiyor.`
            : ""),
      };
    case "strengthen_region":
      return {
        title: `${name} bölgesine ${action.shortfall} net fotoğraf daha ekleyin.`,
        detail: "Bu açıda kullanılabilir dosya var ama sayısı alt sınırın altında.",
      };
    case "assign_unmarked":
      return {
        title: `${action.media_count} dosyanın açısı işaretlenmemiş.`,
        detail:
          "Bu dosyalar hiçbir bölgenin sayısına girmiyor, bu yüzden kapsama olduğundan zayıf görünüyor. Sayfanın altındaki listeden toplu işaretleyebilirsiniz.",
      };
    case "review_uncertain":
      return {
        title: `${action.media_count} dosyanın açısı belirlenemedi.`,
        detail:
          "Bunlar için bir tahmin yapıldı ama güvenilir bulunmadı, bu yüzden hiçbir açıya yazılmadı. Kontrol edip siz işaretleyin.",
      };
    case "run_analysis":
      return {
        title: `${action.media_count} dosyanın kalitesi henüz ölçülmedi.`,
        detail:
          "Analizi çalıştırın; ölçülmemiş dosyaların hiçbir açı hakkında söyleyeceği bir şey yok.",
      };
    case "nothing_to_do":
      return {
        title: "Her açıda çalışmaya yetecek görüntü var.",
        detail:
          "Bu, çekimin bittiği ya da rekonstrüksiyonun başarılı olacağı garantisi değildir.",
      };
  }
}

/** The tone the banner itself carries, so an all-clear does not look urgent. */
export function nextActionTone(action: GuidedNextAction): StatusTone {
  switch (action.kind) {
    case "nothing_to_do":
      return "success";
    case "capture_region":
    case "recapture_region":
    case "start_capturing":
      return "danger";
    case "strengthen_region":
    case "assign_unmarked":
    case "review_uncertain":
      return "caution";
    case "run_analysis":
      return "neutral";
  }
}
